import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/solid';
import Tooltip from 'antd/es/tooltip';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatAmount } from 'utils/formatters/formatAmount';

import { useGetDisbursementPayoutDraftsDraftId } from 'api';

import { useCurrentOrganisation } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { DetailsDraftAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsDraftAttachmentSectionContainer';
import { SuccessCheckmark } from '@atoms/illustrations/SuccessCheckmark';
import { Spinner } from '@atoms/Spinner/Spinner';
import { StatusTag } from '@atoms/StatusTag';

import type { SubmittedPayout } from './utils/types';

import { PAYOUT_STATUSES } from '../constants';

type ManualPayoutSubmittedToReviewProps = {
  submittedPayout: Extract<SubmittedPayout, { state: 'waiting_review' }>;
};

export const ManualPayoutSubmittedToReview = ({
  submittedPayout
}: ManualPayoutSubmittedToReviewProps) => {
  const organisation = useCurrentOrganisation();

  const { data: { data: payout } = {}, isPending } =
    useGetDisbursementPayoutDraftsDraftId(organisation.id, submittedPayout.id);

  const navigate = useNavigate();

  if (isPending || payout === undefined) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex size-full justify-center">
      <div className="flex w-104 flex-col items-center gap-4">
        <SuccessCheckmark className="size-24" />
        <div className="w-full text-center">
          <h1 className="pb-2 text-2xl font-bold">Request submitted</h1>
          <h3 className="text-base font-normal text-content-strong">
            Your payout request has been successfully created and sent for
            approval.
          </h3>
        </div>
        <div className="w-full rounded-2xl border border-border-muted p-5">
          <div className="flex w-full flex-col gap-3">
            <InfoRow label="End-to-end ID" value={payout.end_to_end_id} />
            <InfoRow
              label="Amount"
              value={formatAmount({
                amount: payout.amount,
                currency: payout.currency_code
              })}
            />
            {payout.creditor && (
              <InfoRow label="Receiver" value={payout.creditor.name} />
            )}
            <InfoRow
              label="Status"
              value={
                <StatusTag status={payout.status} statuses={PAYOUT_STATUSES} />
              }
            />
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-1">
              <div className="text-sm font-medium">Attach files</div>
              <Tooltip
                title="This feature is not intended for RFIs"
                placement="right"
                arrow={true}
              >
                <InformationCircleIcon className="size-4 stroke-current" />
              </Tooltip>
            </div>
            <div className="pb-2 text-xs text-content-strong">
              Add supporting documents (optional).
            </div>
            <DetailsDraftAttachmentSectionContainer resourceId={payout.id} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <SecondaryButton
            className="flex h-9 items-center border-2 border-border-brand"
            onClick={() => navigate(`/org/${organisation.id}`)}
          >
            <ArrowLeftIcon className="size-5" />
            <span className="align-middle text-sm">Back to dashboard</span>
          </SecondaryButton>
          <PrimaryButton
            className="flex h-9 items-center text-sm shadow-none"
            type="primary"
            onClick={() => navigate(0)}
          >
            <PlusIcon className="size-5" />
            <span className="align-middle text-sm">Submit new request</span>
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

type InfoRowProps = {
  label: ReactNode;
  value: ReactNode;
};

const InfoRow = ({ label, value }: InfoRowProps) => {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-content-secondary">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
};
